(() => {
  const parseUrl = (value) => {
    try {
      const url = new URL(value);
      return ['http:', 'https:'].includes(url.protocol) ? url : null;
    } catch (_error) {
      return null;
    }
  };

  const getPlatform = (selectedPlatform, url) => {
    const platform = (selectedPlatform || '').toLowerCase();
    if (platform !== 'meta') return platform;

    const hostname = url.hostname.toLowerCase();
    return hostname.includes('instagram.com') ? 'instagram' : 'facebook';
  };

  const getYouTubeId = (url) => {
    const hostname = url.hostname.toLowerCase().replace(/^www\./, '');
    if (hostname === 'youtu.be') return url.pathname.split('/').filter(Boolean)[0];
    if (!hostname.includes('youtube.com') && !hostname.includes('youtube-nocookie.com')) return '';

    const pathParts = url.pathname.split('/').filter(Boolean);
    if (url.searchParams.get('v')) return url.searchParams.get('v');
    if (['shorts', 'embed', 'live'].includes(pathParts[0])) return pathParts[1];
    return '';
  };

  const buildExternalMedia = (rawUrl, selectedPlatform) => {
    const url = parseUrl(rawUrl);
    if (!url) return null;

    const platform = getPlatform(selectedPlatform, url);

    if (platform === 'youtube') {
      const id = getYouTubeId(url);
      if (!id) return null;
      return {
        type: 'iframe',
        src: `https://www.youtube-nocookie.com/embed/${encodeURIComponent(id)}?autoplay=1&playsinline=1&rel=0`
      };
    }

    if (platform === 'tiktok') {
      const id = url.pathname.match(/(?:video|player\/v1)\/(\d+)/)?.[1];
      if (!id) return null;
      return {
        type: 'iframe',
        src: `https://www.tiktok.com/player/v1/${id}?autoplay=1&controls=1&loop=0&music_info=0&description=0`
      };
    }

    if (platform === 'instagram') {
      const match = url.pathname.match(/\/(p|reel|tv)\/([^/?#]+)/i);
      if (!match) return null;
      return {
        type: 'iframe',
        src: `https://www.instagram.com/${match[1]}/${match[2]}/embed/`
      };
    }

    if (platform === 'facebook') {
      const params = new URLSearchParams({
        href: url.href,
        show_text: 'false',
        autoplay: 'true'
      });
      return {
        type: 'iframe',
        src: `https://www.facebook.com/plugins/video.php?${params.toString()}`
      };
    }

    if (platform === 'x') {
      const id = url.pathname.match(/\/status\/(\d+)/)?.[1];
      if (!id) return null;
      return {
        type: 'iframe',
        src: `https://platform.twitter.com/embed/Tweet.html?id=${id}&theme=dark&dnt=true`
      };
    }

    if (platform === 'vimeo') {
      const id = url.pathname.match(/(?:video\/)?(\d+)(?:$|\/)/)?.[1];
      if (!id) return null;
      return {
        type: 'iframe',
        src: `https://player.vimeo.com/video/${id}?autoplay=1&title=0&byline=0&portrait=0`
      };
    }

    if (platform === 'direct video') {
      return { type: 'video', src: url.href };
    }

    return null;
  };

  const mountExternalMedia = (frame) => {
    const mediaConfig = buildExternalMedia(frame.dataset.loveVideoUrl, frame.dataset.loveVideoPlatform);
    if (!mediaConfig) {
      frame.dataset.loveVideoError = 'true';
      return;
    }

    let media;
    if (mediaConfig.type === 'video') {
      media = document.createElement('video');
      media.src = mediaConfig.src;
      media.controls = true;
      media.autoplay = true;
      media.playsInline = true;
    } else {
      media = document.createElement('iframe');
      media.src = mediaConfig.src;
      media.title = 'Embedded social video';
      media.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
      media.allowFullscreen = true;
      media.referrerPolicy = 'strict-origin-when-cross-origin';
    }

    media.className = 'mc-love-wall__embed';
    media.dataset.loveVideoEmbed = 'true';
    frame.prepend(media);
    frame.classList.add('is-playing');
  };

  const initWall = (root) => {
    if (!root || root.dataset.mcLoveReady === 'true') return;

    const buttons = [...root.querySelectorAll('[data-love-filter]')];
    const cards = [...root.querySelectorAll('[data-love-category]')];
    const empty = root.querySelector('[data-love-empty]');

    const applyFilter = (activeButton) => {
      if (!activeButton) return;

      const filter = activeButton.dataset.loveFilter;
      let visibleCount = 0;

      buttons.forEach((button) => {
        const isActive = button === activeButton;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', String(isActive));
      });

      cards.forEach((card) => {
        const isVisible = filter === 'all' || card.dataset.loveCategory === filter;
        card.hidden = !isVisible;
        if (isVisible) visibleCount += 1;
      });

      empty?.classList.toggle('is-visible', visibleCount === 0);
    };

    buttons.forEach((button) => {
      button.addEventListener('click', () => applyFilter(button));
    });

    root.querySelectorAll('[data-love-video-frame]').forEach((frame) => {
      const video = frame.querySelector('[data-love-video]');
      const playButton = frame.querySelector('[data-love-video-play]');
      const externalUrl = frame.dataset.loveVideoUrl;
      if (!playButton) return;

      if (externalUrl) {
        const playExternalVideo = (event) => {
          event?.preventDefault();
          event?.stopPropagation();
          if (frame.querySelector('[data-love-video-embed]')) return;
          mountExternalMedia(frame);
        };

        playButton.addEventListener('click', playExternalVideo);
        frame.addEventListener('click', (event) => {
          if (event.target.closest('iframe, video[controls]')) return;
          playExternalVideo(event);
        });
        return;
      }

      if (!video) return;

      const playVideo = async (event) => {
        event?.preventDefault();
        event?.stopPropagation();
        try {
          video.controls = true;
          await video.play();
        } catch (error) {
          console.warn('The video could not start automatically.', error);
        }
      };

      playButton.addEventListener('click', playVideo);
      frame.addEventListener('click', (event) => {
        if (event.target.closest('video[controls]')) return;
        playVideo(event);
      });
      video.addEventListener('play', () => frame.classList.add('is-playing'));
      video.addEventListener('ended', () => {
        frame.classList.remove('is-playing');
        video.controls = false;
      });
    });

    root.querySelectorAll('[data-love-card-url]').forEach((card) => {
      const openSource = () => {
        const url = card.dataset.loveCardUrl;
        if (url) window.open(url, '_blank', 'noopener,noreferrer');
      };

      card.addEventListener('click', (event) => {
        if (event.target.closest('a, button, video, iframe')) return;
        openSource();
      });
      card.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        openSource();
      });
    });

    if (buttons.length > 0) applyFilter(buttons[0]);
    root.dataset.mcLoveReady = 'true';
  };

  const initAll = (scope = document) => {
    if (scope.matches?.('[data-love-wall]')) initWall(scope);
    scope.querySelectorAll?.('[data-love-wall]').forEach(initWall);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
